/** Access group templates — system-wide masters and tenant-scoped copies.
 *
 * Mode toggle: System-wide | Tenant-scoped (tenant picked via autocomplete).
 * The grid is a client-side DataGridPro tree: templates grouped by their
 * template group, children nested under parents.
 */
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import {
  DataGridPro,
  gridColumnFieldsSelector,
  useGridApiRef,
  type GridColDef,
  type GridSlots,
  type GridToolbarProps,
} from '@mui/x-data-grid-pro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import { rbacApi, ROLE_LABELS, type AgroupTemplate, type TemplateScope } from '@/api/rbac';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PageHeader } from '@/components/PageHeader';
import { PermissionGate } from '@/components/PermissionGate';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import { GridControlsToolbar } from '@/components/DataGrid/GridControlsToolbar';
import { gridRefreshToolbar, useGridAutoRefresh } from '@/components/DataGrid/useGridAutoRefresh';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { useSnackbar } from '@/components/Snackbar';
import { useGridPresets } from '@/hooks/useGridPresets';
import { usePermissions } from '@/hooks/usePermissions';
import { formatRelativeTime } from '@/utils/format';
import { useScopedTenantState } from '@/scope/useScopedTenantState';

import { TemplateEditDialog } from './TemplateEditDialog';

type Row = AgroupTemplate & { __path: string[] };

/** Group-name + parent-chain tree path; ids appended only on name collisions.
 *  Deduped paths are cached so children inherit their parent's deduped prefix. */
function buildTreePaths(templates: AgroupTemplate[]): Row[] {
  const byId = new Map(templates.map((t) => [t.id, t]));
  const paths = new Map<number, string[]>();
  const taken = new Set<string>();
  const pathOf = (t: AgroupTemplate): string[] => {
    const cached = paths.get(t.id);
    if (cached) return cached;
    const parent = t.parent !== null ? byId.get(t.parent) : undefined;
    const prefix = parent ? pathOf(parent) : [t.group_name];
    let path = [...prefix, t.name];
    if (taken.has(path.join(' '))) path = [...prefix, `${t.name} (${t.id})`];
    taken.add(path.join(' '));
    paths.set(t.id, path);
    return path;
  };
  return templates.map((t) => ({ ...t, __path: pathOf(t) }));
}

export function TemplatesListPage() {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_rbac_manage');
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const apiRef = useGridApiRef();

  const [mode, setMode] = useState<'system' | 'tenant'>('system');
  const [top, setTop] = useScopedTenantState();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<{ id: number | null } | null>(null);
  const [deleting, setDeleting] = useState<AgroupTemplate | null>(null);

  const scope: TemplateScope | null = mode === 'system' ? 'system' : (top?.id ?? null);

  const autoRefresh = useGridAutoRefresh({ grid: 'templates' });
  const query = useQuery({
    queryKey: ['agroupTemplates', scope, search],
    queryFn: () => rbacApi.listTemplates({ scope: scope!, search }),
    enabled: scope !== null,
    ...autoRefresh.queryOptions(),
  });
  const rows = useMemo(() => buildTreePaths(query.data?.results ?? []), [query.data]);

  const deleteMutation = useMutation({
    mutationFn: (id: number) => rbacApi.deleteTemplate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agroupTemplates'] });
      snackbar.show('Template deleted', 'success');
    },
    onError: (e) => {
      const msg =
        e instanceof ConsoleApiError && e.status === 409
          ? 'Child templates exist — delete or re-parent them first.'
          : (e as Error).message;
      snackbar.show(msg, 'error');
    },
  });

  // Columns-only preset support: the tree is client-side (no server sort) and
  // its filters are the mode/tenant toggles + search, none of which round-trip
  // through a preset. The grouping column is grid-generated and not covered.
  const presets = useGridPresets({
    grid: 'templates',
    defaultOrder: useMemo(
      () => [
        'codename',
        'args',
        'role',
        'propagated_role',
        'scopes',
        ...(mode === 'tenant' ? ['source', 'managed_group_count'] : []),
        'created_ts',
        'updated_ts',
        ...(canManage ? ['__actions__'] : []),
      ],
      [mode, canManage],
    ),
    defaultHidden: ['source', 'created_ts', 'updated_ts'],
    supportsSort: false,
  });

  const columns = useMemo<GridColDef<Row>[]>(() => {
    // NOTE: treeData renders auto-generated filler rows for the template-group
    // path nodes — their `row` is empty, so every accessor must be guarded.
    const cols: GridColDef<Row>[] = [
      { field: 'codename', headerName: 'Code name', width: 160 },
      {
        field: 'args',
        headerName: 'Arguments',
        width: 140,
        sortable: false,
        valueGetter: (_, row) => row.arguments?.map((a) => a.alias).join(', ') ?? '',
      },
      {
        field: 'role',
        headerName: 'Role',
        width: 110,
        valueGetter: (_, row) => (row.role === undefined ? '' : ROLE_LABELS[row.role]),
      },
      {
        field: 'propagated_role',
        headerName: 'Propagated role',
        width: 130,
        valueGetter: (_, row) =>
          row.propagated_role === undefined ? '' : ROLE_LABELS[row.propagated_role],
      },
      {
        field: 'scopes',
        headerName: 'Permissions',
        width: 110,
        sortable: false,
        valueGetter: (_, row) => row.scope_templates?.length ?? '',
      },
      {
        field: 'created_ts',
        headerName: 'Created',
        width: 140,
        renderCell: (p) => formatRelativeTime(p.row.created_ts),
      },
      {
        field: 'updated_ts',
        headerName: 'Updated',
        width: 140,
        renderCell: (p) => formatRelativeTime(p.row.updated_ts),
      },
    ];
    if (mode === 'tenant') {
      // Source template this tenant copy was materialised from (null on masters).
      cols.push({
        field: 'source',
        headerName: 'Source',
        width: 100,
        sortable: false,
        renderCell: (p) =>
          typeof p.row.source === 'number' ? (
            <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
              #{p.row.source}
            </Box>
          ) : null,
      });
      cols.push({ field: 'managed_group_count', headerName: 'Managed groups', width: 130 });
    }
    if (canManage) {
      cols.push({
        field: '__actions__',
        headerName: '',
        width: 90,
        sortable: false,
        renderCell: (p) =>
          typeof p.row.id === 'number' ? (
            <Button
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                setDeleting(p.row);
              }}
            >
              Delete
            </Button>
          ) : null,
      });
    }
    return cols;
  }, [mode, canManage]);

  return (
    <>
      <PageHeader
        helpStory="rbac-model"
        title="Access Group Templates"
        subtitle="System-wide masters; tenant-scoped copies keep their managed access groups in sync."
        actions={
          <PermissionGate perm="console_rbac_manage">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={scope === null}
              onClick={() => setEditing({ id: null })}
            >
              New template
            </Button>
          </PermissionGate>
        }
      />

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <ToggleButtonGroup
          exclusive
          size="small"
          value={mode}
          onChange={(_, v) => {
            if (v) setMode(v);
          }}
        >
          <ToggleButton value="system">System-wide</ToggleButton>
          <ToggleButton value="tenant">Tenant-scoped</ToggleButton>
        </ToggleButtonGroup>
        {mode === 'tenant' && (
          <div style={{ minWidth: 320 }}>
            <TenantPicker
              label="Tour operator"
              placeholder="Search by name or code…"
              value={top}
              onChange={setTop}
            />
          </div>
        )}
      </Stack>

      <DataGridPro<Row>
        apiRef={apiRef}
        treeData
        rows={scope === null ? [] : rows}
        columns={orderColumns(columns, presets.orderedFields)}
        getTreeDataPath={(row) => row.__path}
        groupingColDef={{ headerName: 'Template', flex: 1.5 }}
        defaultGroupingExpansionDepth={-1}
        loading={query.isFetching}
        sx={{ flex: 1, minHeight: 360 }}
        hideFooter
        columnVisibilityModel={presets.columnVisibilityModel}
        onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
        onColumnOrderChange={() =>
          presets.onColumnOrderChange(
            // The grouping column is grid-generated — keep only real fields.
            gridColumnFieldsSelector(apiRef).filter((f) => !f.startsWith('__row_group')),
          )
        }
        onRowClick={(p) => {
          const row = p.row as Row;
          // Filler group nodes have no real row behind them. Viewers open the
          // dialog READ-ONLY: a template is the explanation for every managed
          // group's permissions, and "you may not manage RBAC" is no reason to
          // be unable to read it.
          if (typeof row.id === 'number') setEditing({ id: row.id });
        }}
        slots={{ toolbar: GridControlsToolbar as GridSlots['toolbar'] }}
        slotProps={{
          // External mode: search is a server param of listTemplates, not a
          // grid quick filter (the tree is assembled client-side).
          toolbar: {
            // No tenant picked in tenant mode → the query is disabled.
            refresh: gridRefreshToolbar(query, autoRefresh, { disabled: scope === null }),
            search: true,
            searchPlaceholder: 'Search templates',
            searchValue: search,
            onSearchChange: setSearch,
            hideFilterButton: true,
            exportFileName: 'agroup-templates',
            presets: presets.toolbar,
          } as GridToolbarProps,
          // A slim top bar, not the blocking overlay, on refetches — auto-refresh
          // would otherwise strobe the whole tree every tick.
          loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'skeleton' },
        }}
      />

      {editing !== null && scope !== null && (
        <TemplateEditDialog
          templateId={editing.id}
          scope={scope}
          readOnly={!canManage}
          onClose={() => setEditing(null)}
        />
      )}
      <ConfirmDialog
        open={deleting !== null}
        title="Delete template?"
        message={
          deleting?.tour_operator
            ? `Delete "${deleting.name}"? Its managed access groups become unmanaged (they are NOT deleted).`
            : `Delete the system template "${deleting?.name ?? ''}"? Existing tenant copies keep working.`
        }
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          if (deleting) deleteMutation.mutate(deleting.id);
          setDeleting(null);
        }}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}
